<script setup lang="ts">
/** Выпадающее меню: слот trigger + список пунктов. Позиционируется под триггером. */
import { nextTick, onBeforeUnmount, ref } from 'vue';
import Icon from './Icon.vue';

export interface MenuItem {
  key: string;
  label: string;
  icon?: string;
  color?: string;
  danger?: boolean;
  disabled?: boolean;
  hint?: string;
  divider?: boolean;
  header?: boolean;
}

const props = defineProps<{ items: MenuItem[]; align?: 'left' | 'right'; width?: number }>();
const emit = defineEmits<{ select: [key: string] }>();

const open = ref(false);
const root = ref<HTMLElement | null>(null);
const pos = ref({ top: 0, left: 0 });

function place() {
  const r = root.value?.getBoundingClientRect();
  if (!r) return;
  const w = props.width ?? 220;
  let left = props.align === 'right' ? r.right - w : r.left;
  left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
  let top = r.bottom + 6;
  const estimated = props.items.length * 34 + 12;
  if (top + estimated > window.innerHeight - 8) top = Math.max(8, r.top - estimated - 6);
  pos.value = { top, left };
}

async function toggle() {
  open.value = !open.value;
  if (open.value) {
    await nextTick();
    place();
    window.addEventListener('mousedown', onOutside, true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
  } else close();
}
function close() {
  open.value = false;
  window.removeEventListener('mousedown', onOutside, true);
  window.removeEventListener('keydown', onKey);
  window.removeEventListener('scroll', close, true);
}
const menuEl = ref<HTMLElement | null>(null);
function onOutside(e: MouseEvent) {
  const t = e.target as Node;
  if (root.value?.contains(t) || menuEl.value?.contains(t)) return;
  close();
}
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') close();
}
function pick(item: MenuItem) {
  if (item.disabled || item.divider || item.header) return;
  emit('select', item.key);
  close();
}
onBeforeUnmount(close);
</script>

<template>
  <span ref="root" class="menu-root" @click.stop="toggle">
    <slot :open="open" />
  </span>
  <Teleport to="body">
    <Transition name="pop">
      <div v-if="open" ref="menuEl" class="menu" :style="{ top: pos.top + 'px', left: pos.left + 'px', width: (width ?? 220) + 'px' }">
        <template v-for="item in items" :key="item.key">
          <div v-if="item.divider" class="divider" />
          <div v-else-if="item.header" class="header">{{ item.label }}</div>
          <button
            v-else
            type="button"
            class="item"
            :class="{ danger: item.danger }"
            :disabled="item.disabled"
            @click.stop="pick(item)"
          >
            <span v-if="item.color" class="swatch" :style="{ background: item.color }" />
            <Icon v-else-if="item.icon" :name="item.icon" :size="15" />
            <span class="grow ellipsis">{{ item.label }}</span>
            <span v-if="item.hint" class="hint">{{ item.hint }}</span>
          </button>
        </template>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.menu-root {
  display: inline-flex;
}
.menu {
  position: fixed;
  z-index: 200;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  padding: 5px;
  max-height: 60vh;
  overflow: auto;
}
.item {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 7px 9px;
  border-radius: 7px;
  cursor: pointer;
  color: var(--text);
}
.item:hover:not(:disabled) {
  background: var(--surface-hover);
}
.item:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.item.danger {
  color: var(--danger);
}
.swatch {
  width: 9px;
  height: 9px;
  border-radius: 3px;
  flex-shrink: 0;
}
.hint {
  color: var(--faint);
  font-size: 11.5px;
}
.divider {
  height: 1px;
  background: var(--divider);
  margin: 4px 2px;
}
.header {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--faint);
  padding: 7px 9px 3px;
}
.pop-enter-active,
.pop-leave-active {
  transition:
    opacity 120ms var(--ease),
    transform 120ms var(--ease);
}
.pop-enter-from,
.pop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
